import * as React from "react";
import { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    IconButton,
    Divider,
    Typography,
    Avatar,
    useMediaQuery,
    useTheme,
    Tooltip,
} from "@mui/material";
import {
    Menu as MenuIcon,
    ChevronLeft,
    ChevronRight,
    Description,
    Receipt,
    Assessment,
    Settings,
    Logout,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import CompanySelector from "./CompanySelector";

// Navigation structure
const navigationItems = [
    { name: "Employees", link: "/employees", icon: <Description /> },
    { name: "Invoicing", link: "/invoicing", icon: <Receipt /> },
    { name: "Payroll Reports", link: "/payroll", icon: <Assessment /> },
    { name: "Company Information", link: "/company-information", icon: <Settings /> },
];

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

interface SidebarProps {
    mobileOpen: boolean;
    onMobileClose: () => void;
    onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose, onCollapseChange }) => {
    const [collapsed, setCollapsed] = useState(false);

    const { currentUser, signOut } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleToggleCollapse = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        if (onCollapseChange) {
            onCollapseChange(newCollapsed);
        }
    };

    const handleNavigate = (link: string) => {
        navigate(link);
        if (isMobile) {
            onMobileClose();
        }
    };

    const handleLogout = () => {
        signOut();
        if (isMobile) {
            onMobileClose();
        }
    };

    const isDev = process.env.REACT_APP_ENV === "DEVELOPMENT";
    const accentColor = "#86eb85";

    const drawerContent = (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#1a1a1a",
                color: "#fff",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRight: { xs: `4px solid ${accentColor}`, md: "none" },
                pt: { xs: 2, md: 1 },
            }}
        >
            {/* Collapse Button - Desktop Only */}
            {!isMobile && (
                <Box
                    sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "flex-end",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                    }}
                >
                    <IconButton
                        onClick={handleToggleCollapse}
                        sx={{
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.1)",
                            },
                        }}
                    >
                        {collapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                </Box>
            )}

            {/* Logo/Title */}
            {!collapsed && (
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={require("../assets/logoLarge.png")}
                        alt="CycleSaver"
                        sx={{
                            height: "40px",
                            width: "auto",
                            objectFit: "contain",
                        }}
                    />
                </Box>
            )}

            {/* Company Selector - At the top */}
            <CompanySelector collapsed={collapsed} />

            {/* Environment Badge */}
            {isDev && !collapsed && (
                <Box
                    sx={{
                        mx: 2,
                        mb: 2,
                        py: 0.5,
                        px: 1.5,
                        backgroundColor: "#AAFFFF",
                        color: "#000",
                        borderRadius: 1,
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                    }}
                >
                    DEVELOPMENT
                </Box>
            )}

            {/* Navigation Items */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    py: 1,
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "rgba(255,255,255,0.05)",
                        borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(255,255,255,0.2)",
                        borderRadius: "4px",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.3)",
                        },
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255,255,255,0.2) rgba(255,255,255,0.05)",
                }}
            >
                <List sx={{ px: 1 }}>
                    {navigationItems.map((item) => {
                        const isActive = location.pathname === item.link;
                        return (
                            <Tooltip
                                key={item.name}
                                title={collapsed ? item.name : ""}
                                placement="right"
                                arrow
                            >
                                <ListItem disablePadding sx={{ mb: 0.5 }}>
                                    <ListItemButton
                                        onClick={() => handleNavigate(item.link)}
                                        sx={{
                                            borderRadius: 1,
                                            px: 2,
                                            py: 1,
                                            minHeight: "48px",
                                            backgroundColor: isActive
                                                ? "rgba(134, 235, 133, 0.15)"
                                                : "transparent",
                                            borderLeft: isActive ? "3px solid #86eb85" : "3px solid transparent",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: isActive
                                                    ? "rgba(134, 235, 133, 0.2)"
                                                    : "rgba(255,255,255,0.08)",
                                                transform: "translateX(4px)",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: isActive ? "#86eb85" : "rgba(255,255,255,0.7)",
                                                minWidth: collapsed ? 0 : 40,
                                                justifyContent: "center",
                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {!collapsed && (
                                            <ListItemText
                                                primary={item.name}
                                                sx={{
                                                    "& .MuiTypography-root": {
                                                        fontSize: "15px",
                                                        fontWeight: isActive ? 600 : 500,
                                                        fontFamily: "Montserrat",
                                                        color: isActive ? "#86eb85" : "#fff",
                                                        transition: "all 0.2s ease",
                                                    },
                                                }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            </Tooltip>
                        );
                    })}
                </List>
            </Box>

            {/* User Section */}
            <Box
                sx={{
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                    p: 1.5,
                }}
            >
                {!collapsed && currentUser && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 1,
                            px: 1,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                backgroundColor: "#86eb85",
                                color: "#1a1a1a",
                                fontWeight: 600,
                                fontSize: "14px",
                            }}
                        >
                            {currentUser.firstName?.[0]}{currentUser.surname?.[0]}
                        </Avatar>
                        <Box sx={{ overflow: "hidden", flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    fontFamily: "Montserrat",
                                    color: "#fff",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.3,
                                }}
                            >
                                {currentUser.firstName} {currentUser.surname}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    color: "rgba(255,255,255,0.6)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.3,
                                }}
                            >
                                {currentUser.email}
                            </Typography>
                            {currentUser.company?.companyName && (
                                <Typography
                                    sx={{
                                        fontSize: "10px",
                                        color: "#86eb85",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        lineHeight: 1.3,
                                        mt: 0.25,
                                    }}
                                >
                                    {currentUser.company.companyName}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                <Tooltip title={collapsed ? "Logout" : ""} placement="right" arrow>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            borderRadius: 1,
                            px: 2,
                            py: 0.75,
                            minHeight: "40px",
                            justifyContent: collapsed ? "center" : "flex-start",
                            "&:hover": {
                                backgroundColor: "rgba(255,69,58,0.15)",
                                "& .MuiListItemIcon-root": {
                                    color: "#ff453a",
                                },
                                "& .MuiTypography-root": {
                                    color: "#ff453a",
                                },
                            },
                            transition: "all 0.2s ease",
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                color: "rgba(255,255,255,0.7)",
                                minWidth: collapsed ? 0 : 40,
                                justifyContent: "center",
                                transition: "color 0.2s ease",
                            }}
                        >
                            <Logout />
                        </ListItemIcon>
                        {!collapsed && (
                            <ListItemText
                                primary="Logout"
                                sx={{
                                    "& .MuiTypography-root": {
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        fontFamily: "Montserrat",
                                        color: "#fff",
                                        transition: "color 0.2s ease",
                                    },
                                }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onMobileClose}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: DRAWER_WIDTH,
                            boxSizing: "border-box",
                            border: "none",
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                /* Desktop Drawer */
                <Drawer
                    variant="permanent"
                    sx={{
                        width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                        flexShrink: 0,
                        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "& .MuiDrawer-paper": {
                            width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                            boxSizing: "border-box",
                            border: "none",
                            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            overflowX: "hidden",
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default Sidebar;

